import "./App.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { ConfigProvider, Layout } from "antd";
import Home from "./pages/Home";
import Navbar from "./components/common/Navbar";
import Sidebar from "./components/common/Sidebar";
import PastDuePaymentBanner from "./components/common/PastDuePaymentBanner";
import "@xyflow/react/dist/style.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useProfile } from "./api/profileApi";
import Login from "./pages/Login";
import SignUp from "./pages/Signup";
import { theme } from "./antdTheme";
import { SessionProvider } from "./context/SessionContext";
import Session from "./pages/Session";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ReportList from "./pages/Reports/ReportList";
import Report from "./pages/Reports/Report";
import EmailVerification from "./pages/EmailVerification";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import { Profile } from "./pages/Profile";
import { Plans } from "./pages/Plans"
import RubricSessionList from "./pages/Rubrics/RubricSessionList";
import RubricGenerator from "./pages/Rubrics/RubricGenerator";
import CohortList from "./pages/Cohort/CohortList";
import ManageCohort from "./pages/Cohort/ManageCohort";
import JoinCohort from "./pages/Cohort/JoinCohort";
import CohortReportView from "./pages/Cohort/CohortReportView";
import ComparisonList from "./pages/Comparison/ComparisonList";
import ComparisonView from "./pages/Comparison/ComparisonView";
import { Font } from "@react-pdf/renderer";

const { Header, Content } = Layout;
const queryClient = new QueryClient();

function AppLayout({ children }) {
  const location = useLocation();
  const isNoGUIPage =
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/verify-email" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password" ||
    location.pathname === "/onboarding" ||
    location.pathname === "/join";

  if (isNoGUIPage) {
    return <div className="w-full">{children}</div>;
  }

  return (
    <ConfigProvider theme={theme}>
      <Layout className="h-screen" hasSider>
        <Sidebar />
        <Layout className="relative">
          <Header className="absolute top-0 left-0 right-0 z-30 bg-transparent p-0 h-16">
            <Navbar />
          </Header>
          <Content className="bg-background min-h-[280px] overflow-auto pt-16">
            <PastDuePaymentBanner />
            {children}
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}

function PrivateRoute({ children }) {
  const { currentUser, loading } = useAuth();
  const { data: profileData, isLoading: isProfileLoading } = useProfile();
  const location = useLocation();

  // Show loading screen while auth is initializing
  if (loading || (currentUser && isProfileLoading)) {
    return (
      <div className="flex justify-center items-center h-screen flex-col">
        <h1 className="text-4xl font-extrabold text-center my-6 text-primary">
          Dittrium
        </h1>
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (!profileData?.onboarding_completed_at && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" />;
  }

  return children;
}

function PlanProtectedRoute({ children, requiredPlans }) {
  const { currentUser, loading } = useAuth();
  const { data: profileData } = useProfile();
  const userPlan = profileData?.plan || 'free';
  
  // Show loading screen while auth is initializing
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen flex-col">
        <h1 className="text-4xl font-extrabold text-center my-6 text-primary">
          Dittrium
        </h1>
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (!profileData?.onboarding_completed_at) {
    return <Navigate to="/onboarding" />;
  }
  
  if (!requiredPlans.includes(userPlan)) {
    return <Navigate to="/" />;
  }
  
  return children;
}

function App() {

  Font.register({
    family: "Poppins",
    fonts: [
      {
        src: require("./assets/fonts/Poppins/Poppins-Regular.ttf"),
        fontWeight: 400,
      },
      {
        src: require("./assets/fonts/Poppins/Poppins-Medium.ttf"),
        fontWeight: 500,
      },
      {
        src: require("./assets/fonts/Poppins/Poppins-SemiBold.ttf"),
        fontWeight: 600,
      },
      {
        src: require("./assets/fonts/Poppins/Poppins-Bold.ttf"),
        fontWeight: 800,
      },
    ],
  });

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SessionProvider>
          <BrowserRouter>
            <AppLayout>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/verify-email" element={<EmailVerification />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/onboarding"
                  element={
                    <PrivateRoute>
                      <Onboarding />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <Home />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/join"
                  element={
                    <PrivateRoute>
                      <JoinCohort />
                    </PrivateRoute>
                  }
                />
                <Route path="/session/:id/:phase" element={<Session />} />
                <Route
                  path="/reports"
                  element={
                    <PrivateRoute>
                      <ReportList />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/reports/:sessionId"
                  element={
                    <PrivateRoute>
                      <Report />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/rubric-generator"
                  element={
                    <PlanProtectedRoute requiredPlans={["plus", "professional"]}>
                      <RubricSessionList />
                    </PlanProtectedRoute>
                  }
                />
                <Route
                  path="/rubric-generator/:sessionId"
                  element={
                    <PlanProtectedRoute requiredPlans={["plus", "professional"]}>
                      <RubricGenerator />
                    </PlanProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/plans"
                  element={
                    <PrivateRoute>
                      <Plans/>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/cohorts"
                  element={
                    <PlanProtectedRoute requiredPlans={["professional"]}>
                      <CohortList />
                    </PlanProtectedRoute>
                  }
                />
                <Route
                  path="/cohorts/:cohortId"
                  element={
                    <PlanProtectedRoute requiredPlans={["professional"]}>
                      <ManageCohort />
                    </PlanProtectedRoute>
                  }
                />
                <Route
                  path="/cohorts/:cohortId/report"
                  element={
                    <PlanProtectedRoute requiredPlans={["professional"]}>
                      <CohortReportView />
                    </PlanProtectedRoute>
                  }
                />
                <Route
                  path="/compare"
                  element={
                    <PlanProtectedRoute requiredPlans={["professional"]}>
                      <ComparisonList />
                    </PlanProtectedRoute>
                  }
                />
                <Route
                  path="/comparison"
                  element={
                    <PlanProtectedRoute requiredPlans={["professional"]}>
                      <ComparisonView />
                    </PlanProtectedRoute>
                  }
                />
              </Routes>
            </AppLayout>
          </BrowserRouter>
        </SessionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
